import React, {useContext, useState} from "react";
import {GlobalStateContext} from "../../../global/context/GlobalStateContext";
import {AutoLoaderDeviceOverviewContext} from "../context/AutoLoaderDeviceOverviewContext";
import {assetTypeToConfigTypeDict, getSortedListWithOther} from "../../../util/Util";
import {
    IP_ADDRESS_PATTERN,
    isValidEntry,
    MAC_MASK,
    PATTERNS, PROTOCOLS, SPEC_VERSION_DISPLAY_VALUES,
    validateNewAutoLoaderDevice, VENDOR_DISPLAY_VALUES, VENDOR_INFO_KEYS
} from "../../../util/Validations";
import { injectIntl} from "react-intl";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Input from "@amzn/meridian/input";
import Select, {SelectOption} from "@amzn/meridian/select";
import Alert from "@amzn/meridian/alert";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {getLocalizedMessage} from "../../../util/Localization";

const AddAutoLoaderSingleDeviceModal = (props) => {

    const {open, setOpen, selected, setSelected} = props;
    const {showWarning} = useContext(GlobalStateContext);
    const {viewModel, dispatch} = useContext(AutoLoaderDeviceOverviewContext);

    // Visual controls
    const [showAlert, setShowAlert] = useState(false)

    // States
    const [name, setName] = useState("")
    const [mac, setMac] = useState("")
    const [ip, setIp] = useState("")
    const [port, setPort] = useState("")
    const [deviceType, setDeviceType] = useState("")
    const [id, setId] = useState("")
    const [vendor, setVendor] = useState("");
    const [specVersion, setSpecVersion] = useState("");

    // Error
    const [nameErr, setNameErr] = useState(false)
    const [macErr, setMacErr] = useState(false)
    const [ipErr, setIpErr] = useState(false)
    const [portErr, setPortErr] = useState(false)
    const [deviceTypeErr, setDeviceTypeErr] = useState(false)
    const [idErr, setIdErr] = useState(false)
    const [vendorErr, setVendorErr] = useState(false);
    const [specVersionErr, setSpecVersionErr] = useState(false);

    const initialMonitoringValue = {
        startTime: "0",
        endTime: "0",
        reason: ""
    }
    const [monitoring, setMonitoring] = useState(JSON.stringify(initialMonitoringValue));
    const { showCustomAlert } = useContext(GlobalStateContext);

    const stateValues = {
        name: name,
        mac: mac,
        ip: ip,
        port: port,
        deviceType: deviceType,
        deviceConfig: {
            type: assetTypeToConfigTypeDict.typeAUTO_LOADER,
            id: id
        },
        vendorAttributes: {
            vendor: vendor,
            spec_version: specVersion
        },
        monitoring: monitoring
    }

    const onClickConfirm = (stateValues) => {

        if (validateNewAutoLoaderDevice(stateValues)) {
            showWarning(getLocalizedMessage({intl: props.intl, stringId: "device.editWarning"}), () => {
                setShowAlert(false)
                stateValues.deviceConfig = JSON.stringify(stateValues.deviceConfig)
                if (selected) {
                    updateData(stateValues);
                } else {
                    addData(stateValues);
                }
                resetInputState();
                setOpen(false);
            });
        } else {
            setShowAlert(true)
        }
    }

    const addData = (stateValues) => {

        dispatch({type: 'ADD_DEVICE_DATA', data: {
                dispatcher: dispatch, stateValues: stateValues,
                showCustomAlert: showCustomAlert}})
    }

    const updateData = (stateValues) => {

        dispatch({type: 'UPDATE_DEVICE_DATA', data: {dispatcher: dispatch, stateValues: stateValues,
                showCustomAlert: showCustomAlert}})
    }

    const onCloseModal = () => {
        setOpen(false)
        setSelected("");
        resetInputState();
        setShowAlert(false);
    }

    function resetInputState() {
        // setting state of input fields as empty to have empty inputs on clicking Add Device again
        setMac("");
        setMacErr(false);
        setMonitoring(JSON.stringify(initialMonitoringValue));
        resetAssetDetails();
        resetAssetDetailErrors();
    }

    const resetAssetDetails = () => {
        setName("");
        setIp("");
        setPort("");
        setDeviceType("");
        setId("");
        setVendor("");
        setSpecVersion("");
    }

    const resetAssetDetailErrors = () => {
        setNameErr(false);
        setIpErr(false);
        setPortErr(false);
        setDeviceTypeErr(false);
        setIdErr(false);
        setVendorErr(false);
        setSpecVersionErr(false);
    }

    const prefill = (value) => {
        if (viewModel.autoLoaderDeviceInformation.hasOwnProperty(value)) {
            setSelected(value);
            const device = viewModel.autoLoaderDeviceInformation[value];
            const vendorAttributes = device.vendorAttributes;
            resetAssetDetailErrors();
            setName(device.name);
            setMac(device.mac)
            setIp(device.ip);
            setPort(device.port);
            setDeviceType(device.deviceType);
            setId(device.id);
            setVendor(vendorAttributes.vendor);
            setSpecVersion(vendorAttributes.spec_version);
            const deviceMonitoring = {
                startTime: device.startTime,
                endTime: device.endTime,
                reason: device.reason
            }
            setMonitoring(JSON.stringify(deviceMonitoring));
        } else if (selected) {
            setSelected("");
            resetAssetDetails();
        }
    }

    return (
        <Modal
            title={getLocalizedMessage({intl: props.intl, stringId: "device.add_device_button_modal"})}
            open={open}
            onOpen={() => prefill(selected)}
            onClose={onCloseModal}
            scrollContainer="viewport"
            closeLabel="Close"
            bodySpacingInset="medium"
            width="100%">
            <Column spacing={"medium"} spacingInset={"medium"}>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="mac-input">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.mac"})}
                    </Text>
                    <Input id="mac-input"
                           value={mac}
                           onChange={setMac}
                           mask={MAC_MASK}
                           disabled={selected}
                           onBlur={() => {
                               setMacErr(!isValidEntry(stateValues.mac, PATTERNS.MAC))
                               prefill(mac);
                           }}
                           error={macErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="ip-input">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.ip"})}
                    </Text>
                    <Input id="ip-input"
                           value={ip}
                           onChange={setIp}
                           onBlur={() => setIpErr(!isValidEntry(stateValues.ip, IP_ADDRESS_PATTERN))}
                           error={ipErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="port-input">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.port"})}
                    </Text>
                    <Input id="port-input"
                           value={port}
                           onChange={setPort}
                           onBlur={() => setPortErr(!isValidEntry(stateValues.port, PATTERNS.PORT))}
                           error={portErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.protocol"})}
                    </Text>
                    <Select id="device-type-input"
                            value={deviceType}
                            onChange={(value) => setDeviceType(value)}
                            onBlur={() => setDeviceTypeErr(!deviceType)}
                            placeholder={getLocalizedMessage({intl: props.intl, stringId: "modal.select_protocol"})}
                            error={deviceTypeErr}
                            width="100%">
                        {
                            PROTOCOLS.AUTO_LOADER.map(protocol => <SelectOption key={protocol} value={protocol} label={protocol}/>)
                        }
                    </Select>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="name-input">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.name"})}
                    </Text>
                    <Input id="name-input"
                           value={name}
                           onChange={setName}
                           onBlur={() => setNameErr(!isValidEntry(stateValues.name, PATTERNS.NAME))}
                           error={nameErr}
                           helperText={getLocalizedMessage({intl: props.intl, stringId:  "modal.name_induct_line_desc"})}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="id-input">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.device_id"})}
                    </Text>
                    <Input id="id-input"
                           value={id}
                           onChange={setId}
                           onBlur={() => setIdErr(!isValidEntry(stateValues.deviceConfig.id, PATTERNS.DEVICE_ID))}
                           error={idErr}
                           helperText={getLocalizedMessage({intl: props.intl, stringId: "modal.device_id_induct_line_desc"})}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        {getLocalizedMessage({intl: props.intl, stringId: 'modal.vendor'})}
                    </Text>
                    <Select id="device-type-input"
                            value={vendor}
                            onChange={(value) => setVendor(value)}
                            onBlur={() => setVendorErr(!vendor)}
                            placeholder={getLocalizedMessage({intl: props.intl, stringId: 'modal.select_vendor'})}
                            error={vendorErr}
                            width="100%">
                        { getSortedListWithOther(VENDOR_INFO_KEYS.VENDOR).map(value => <SelectOption key={value} value={value} label={VENDOR_DISPLAY_VALUES[value]}/>) }
                    </Select>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        {getLocalizedMessage({intl: props.intl, stringId: 'modal.spec_version'})}
                    </Text>
                    <Select id="device-type-input"
                            value={specVersion}
                            onChange={(value) => setSpecVersion(value)}
                            onBlur={() => setSpecVersionErr(!specVersion)}
                            placeholder={getLocalizedMessage({intl: props.intl, stringId: 'modal.select_version'})}
                            error={specVersionErr}
                            width="100%">
                        { getSortedListWithOther(VENDOR_INFO_KEYS.SPEC_VERSION).map(value => <SelectOption key={value} value={value} label={SPEC_VERSION_DISPLAY_VALUES[value]}/>) }
                    </Select>
                </Column>
            </Column>
            <ModalFooter>
                <Column>
                    {showAlert && <Alert type="warning" size="small">
                        {getLocalizedMessage({intl: props.intl, stringId: 'alert.missingfields'})}
                    </Alert>}
                </Column>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" size="medium" onClick={onCloseModal}>
                        {getLocalizedMessage({intl: props.intl, stringId: 'modal.cancel'})}
                    </Button>
                    <Button type="primary"
                            size="medium"
                            onClick={() => onClickConfirm(stateValues)}>
                        {selected ? getLocalizedMessage({intl: props.intl, stringId: 'modal.confirmEdit'}) :
                            getLocalizedMessage({intl: props.intl, stringId: 'modal.confirmAdd'})
                        }
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    )
}

export default injectIntl(AddAutoLoaderSingleDeviceModal);


// WEBPACK FOOTER //
// ./src/components/devicemanagement/autoloader/modal/AddAutoLoaderSingleDeviceModal.js